import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UserSessionService } from './mock-auth.service';
import { environment } from '../../../environments/environment';

/**
 * Registration Step Enums and DTOs based on API spec
 */
export enum RegistrationStep {
  PORTAL_ACCESS = 'PORTAL_ACCESS',
  DATA_INPUT = 'DATA_INPUT',
  VERIFICATION = 'VERIFICATION',
  MFA_SETUP = 'MFA_SETUP',
  CONTRACT_SENT = 'CONTRACT_SENT',
  CONTRACT_SIGNATURE = 'CONTRACT_SIGNATURE',
  CONTRACT_COUNTERSIGNATURE = 'CONTRACT_COUNTERSIGNATURE',
  CONTRACT_ACTIVATED = 'CONTRACT_ACTIVATED',
}

export enum RegistrationStatus {
  PENDING = 'PENDING',
  USER_INPUT = 'USER_INPUT',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  MODIFICATION_REQUESTED = 'MODIFICATION_REQUESTED',
}

export interface RegistrationStepResponse {
  id: number;
  professionalId: string;
  step: RegistrationStep;
  stepDescription: string;
  status: RegistrationStatus;
  comments: string | null;
  isCurrent: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /**
   * True quando, in fase di validazione MFA, il Back Office ha modificato i codici SOL
   * dopo la conferma del fiduciario: la compilazione MFA non è più valida e va richiesta
   * una nuova compilazione (l'approvazione resta disabilitata anche dopo un reload).
   */
  mfaReviewDirty?: boolean;
}

export interface RegistrationStepUpdateRequest {
  status?: RegistrationStatus;
  comments?: string;
}

/**
 * Service per gestire gli step di registrazione dei fiduciari
 *
 * API Endpoints:
 * - GET /api/professionals/{id}/registration-step/current - Step corrente
 * - GET /api/professionals/{id}/registration-step/history - Storico completo
 * - PUT /api/professionals/{id}/registration-step - Aggiorna step
 * - POST /api/professionals/{id}/registration-step/advance - Avanza manualmente
 * - POST /api/professionals/{id}/registration-step/rollback - Torna a uno step precedente
 */
@Injectable({
  providedIn: 'root',
})
export class RegistrationStepService {
  private readonly http = inject(HttpClient);
  private readonly mockAuthService = inject(UserSessionService);
  private readonly baseUrl = `${environment.professionalsApiUrl}/api/v1/professionals`;

  /**
   * Recupera l'ID del fiduciario da usare per la demo
   * Se fornito, usa quello passato, altrimenti usa quello salvato nel login
   *
   * @param professionalId ID opzionale da usare
   * @returns ID del fiduciario o null se non disponibile
   */
  private resolveProfessionalId(professionalId?: string | null): string | null {
    if (professionalId) {
      return professionalId;
    }

    // Fallback to demo ID from session storage
    const demoId = this.mockAuthService.getDemoProfessionalId();
    if (demoId) {
      console.log(`[RegistrationStepService] Using demo professional ID: ${demoId}`);
      return demoId;
    }

    return null;
  }

  /**
   * Recupera lo step attualmente attivo per un fiduciario
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * @param professionalId ID del fiduciario (es. "FD-00001") - opzionale
   * @returns Observable con lo step corrente
   */
  getCurrentStep(professionalId?: string | null): Observable<RegistrationStepResponse> {
    const id = this.resolveProfessionalId(professionalId);
    if (!id) {
      throw new Error(
        'Professional ID is required. Please provide an ID or set it in the login form.',
      );
    }

    const url = `${this.baseUrl}/${id}/registration-step/current`;
    console.log(`[RegistrationStepService] GET ${url}`);

    return this.http.get<RegistrationStepResponse>(url);
  }

  /**
   * Recupera l'intera cronologia degli step per un fiduciario
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * @param professionalId ID del fiduciario - opzionale
   * @returns Observable con array di step (ordinato per createdAt ascendente)
   */
  getStepHistory(professionalId?: string | null): Observable<RegistrationStepResponse[]> {
    const id = this.resolveProfessionalId(professionalId);
    if (!id) {
      throw new Error(
        'Professional ID is required. Please provide an ID or set it in the login form.',
      );
    }

    return this.http.get<RegistrationStepResponse[]>(
      `${this.baseUrl}/${id}/registration-step/history`,
    );
  }

  /**
   * Aggiorna lo stato e/o i commenti dello step corrente
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * Comportamento:
   * - Solo comments: Aggiorna i commenti della riga corrente (no nuova riga)
   * - status → COMPLETED: Marca step come completato e avanza automaticamente al prossimo
   * - status → REJECTED: Crea nuova riga stesso step con status REJECTED e blocca processo
   *
   * @param professionalId ID del fiduciario - opzionale
   * @param request Dati da aggiornare (status e/o comments)
   * @returns Observable con il nuovo step (se avanzato) o step aggiornato
   */
  updateStep(
    professionalId: string | null | undefined,
    request: RegistrationStepUpdateRequest,
  ): Observable<RegistrationStepResponse> {
    const id = this.resolveProfessionalId(professionalId);
    if (!id) {
      throw new Error(
        'Professional ID is required. Please provide an ID or set it in the login form.',
      );
    }

    return this.http.put<RegistrationStepResponse>(
      `${this.baseUrl}/${id}/registration-step`,
      request,
    );
  }

  /**
   * Forza l'avanzamento al prossimo step (solo se step corrente è COMPLETED)
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * Condizioni:
   * - Step corrente deve avere status=COMPLETED
   * - Non deve essere l'ultimo step (CONTRACT_SIGNATURE)
   *
   * @param professionalId ID del fiduciario - opzionale
   * @returns Observable con il nuovo step creato
   * @throws 400 Bad Request se non può avanzare
   */
  advanceStep(professionalId?: string | null): Observable<RegistrationStepResponse> {
    const id = this.resolveProfessionalId(professionalId);
    if (!id) {
      throw new Error(
        'Professional ID is required. Please provide an ID or set it in the login form.',
      );
    }

    return this.http.post<RegistrationStepResponse>(
      `${this.baseUrl}/${id}/registration-step/advance`,
      {},
    );
  }

  /**
   * Riporta il workflow del fiduciario a uno step precedente già presente in storico.
   * Solo Back Office. Il BE riattiva la riga target e ne reimposta lo status di default
   * (es. CONTRACT_SENT → PENDING); se il fiduciario era ACTIVE lo riporta a PROCESSING.
   *
   * Usato dopo l'eliminazione di un contratto per riallineare lo step di registrazione
   * (i microservizi contratti/professionals sono disaccoppiati e la delete non lo fa).
   *
   * @param professionalId ID del fiduciario - opzionale (fallback su ID demo)
   * @param targetStep Step di destinazione (deve essere già presente nello storico)
   * @param reason Motivo del rollback (opzionale, salvato nei commenti dello step)
   * @returns Observable con lo step riattivato
   * @throws 400/409 se il target coincide con lo step corrente o è successivo
   */
  rollbackToStep(
    professionalId: string | null | undefined,
    targetStep: RegistrationStep,
    reason?: string,
  ): Observable<RegistrationStepResponse> {
    const id = this.resolveProfessionalId(professionalId);
    if (!id) {
      throw new Error(
        'Professional ID is required. Please provide an ID or set it in the login form.',
      );
    }

    return this.http.post<RegistrationStepResponse>(
      `${this.baseUrl}/${id}/registration-step/rollback`,
      { targetStep, reason: reason ?? null },
    );
  }

  /**
   * Helper: Completa lo step corrente
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * @param professionalId ID del fiduciario - opzionale
   * @param comments Commenti opzionali
   * @returns Observable con il prossimo step (auto-avanzamento)
   */
  completeCurrentStep(
    professionalId?: string | null,
    comments?: string,
  ): Observable<RegistrationStepResponse> {
    return this.updateStep(professionalId, {
      status: RegistrationStatus.COMPLETED,
      comments: comments || 'Step completato',
    });
  }

  /**
   * Helper: Rigetta lo step corrente
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * @param professionalId ID del fiduciario - opzionale
   * @param reason Motivo del rigetto
   * @returns Observable con step respinto
   */
  rejectCurrentStep(
    professionalId?: string | null,
    reason?: string,
  ): Observable<RegistrationStepResponse> {
    return this.updateStep(professionalId, {
      status: RegistrationStatus.REJECTED,
      comments: reason || 'Step respinto',
    });
  }

  /**
   * Helper: Cambia step a USER_INPUT (per correzioni dopo rigetto)
   * Se professionalId non è fornito, usa l'ID demo salvato nel login
   *
   * @param professionalId ID del fiduciario - opzionale
   * @param comments Commenti opzionali
   * @returns Observable con step aggiornato
   */
  markForUserInput(
    professionalId?: string | null,
    comments?: string,
  ): Observable<RegistrationStepResponse> {
    return this.updateStep(professionalId, {
      status: RegistrationStatus.USER_INPUT,
      comments: comments || 'In attesa di correzioni',
    });
  }

  /**
   * Helper: Verifica se uno step è completato
   */
  isStepCompleted(step: RegistrationStepResponse): boolean {
    return step.status === RegistrationStatus.COMPLETED;
  }

  /**
   * Helper: Verifica se uno step è respinto
   */
  isStepRejected(step: RegistrationStepResponse): boolean {
    return step.status === RegistrationStatus.REJECTED;
  }

  /**
   * Helper: Verifica se uno step richiede input utente
   */
  requiresUserInput(step: RegistrationStepResponse): boolean {
    return step.status === RegistrationStatus.USER_INPUT;
  }

  /**
   * Helper: Ottieni il colore per lo status
   */
  getStatusColor(status: RegistrationStatus): string {
    const colors = {
      [RegistrationStatus.PENDING]: '#9CA3AF', // Grigio
      [RegistrationStatus.USER_INPUT]: '#F59E0B', // Giallo/Arancione
      [RegistrationStatus.REJECTED]: '#EF4444', // Rosso
      [RegistrationStatus.COMPLETED]: '#10B981', // Verde
      [RegistrationStatus.MODIFICATION_REQUESTED]: '#F97316', // Arancione
    };
    return colors[status];
  }

  /**
   * Helper: Ottieni l'icona per lo status
   */
  getStatusIcon(status: RegistrationStatus): string {
    const icons = {
      [RegistrationStatus.PENDING]: 'pi-clock',
      [RegistrationStatus.USER_INPUT]: 'pi-pencil',
      [RegistrationStatus.REJECTED]: 'pi-times-circle',
      [RegistrationStatus.COMPLETED]: 'pi-check-circle',
      [RegistrationStatus.MODIFICATION_REQUESTED]: 'pi-refresh',
    };
    return icons[status];
  }

  /**
   * Helper: Ottieni la label per lo status
   */
  getStatusLabel(status: RegistrationStatus): string {
    const labels = {
      [RegistrationStatus.PENDING]: 'In attesa',
      [RegistrationStatus.USER_INPUT]: 'Azione richiesta',
      [RegistrationStatus.REJECTED]: 'Respinto',
      [RegistrationStatus.COMPLETED]: 'Completato',
      [RegistrationStatus.MODIFICATION_REQUESTED]: 'Modifica richiesta',
    };
    return labels[status];
  }
}
